import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import { SwitchVerticalIcon, TrashIcon } from "@heroicons/react/outline";
import { WorkflowObjectSchema } from "schemas/Workflow";
import { apiDel, apiPost, apiPut, messageFromException } from "helpers/Request";
import { toast } from "react-hot-toast";
import { useAuth } from "hooks/Auth";
import { getWorkflows } from "redux/actions/content-flow";
import SecondaryButton from "components/Buttons/SecondaryButton";
import PrimaryButton from "components/Buttons/PrimaryButton";
import { modal } from "components/HeadlessModal";
import LabelEdit from "components/FormElements/LabelEdit";
import { set } from "lodash";

export default function WorkflowsPanel(props) {
    const cfState = useSelector((state) => state.contentFlow);
    const stateWorkflows = cfState.workflows || [];
    const [workflows, setWorkflows] = useState(stateWorkflows || []);
    const [listItems, setListItems] = useState([]);
    const [removeItems, setRemoveItems] = useState([]);
    const newWorkflow = useRef(null);
    const auth = useAuth();
    const dispatch = useDispatch();

    const handleSave = () => {
        const id = toast.loading("Updating workflows...");

        let promises = listItems.map((item, index) => {
            const existingWorkflow = workflows.find((wf) => wf.uuid === item);
            if (existingWorkflow) {
                return apiPut(
                    "/v4/workflow/" + existingWorkflow.uuid,
                    auth.publication,
                    Object.assign({}, WorkflowObjectSchema, {
                        priority: index,
                        name: existingWorkflow.name,
                    })
                );
            } else {
                return apiPost(
                    "/v4/workflow",
                    auth.publication,
                    Object.assign({}, WorkflowObjectSchema, {
                        name: item,
                        is_print: 1,
                        is_web: 1,
                        is_content: 1,
                        is_assignment: 1,
                        priority: index,
                        status: 1,
                    })
                );
            }
        });

        removeItems.forEach((item) => {
            console.log(item);
            promises.push(apiDel("/v4/workflow/" + item, auth.publication));
        });

        Promise.all(promises)
            .then(() => {
                toast.success("Workflows updated", { id });
                dispatch(getWorkflows());
            })
            .catch((err) => {
                toast.dismiss(id);
                modal.error({
                    title: "Error updating workflows",
                    children: messageFromException(err),
                });
            });
    };

    const requestRemove = (item) => {
        const newItems = listItems.filter((i) => i !== item);
        setListItems(newItems);
        setRemoveItems([...removeItems, item]);
    };

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);

        return result;
    };

    const onDragEnd = (result) => {
        if (!result.destination) {
            return;
        }

        const items = reorder(
            listItems,
            result.source.index,
            result.destination.index
        );

        setListItems(items);
    };

    const addWorkflowStep = () => {
        const name = newWorkflow.current.value;
        if (!name) {
            return;
        }
        setListItems([...listItems, name]);
        newWorkflow.current.value = "";
    };

    useEffect(() => {
        setWorkflows(stateWorkflows || []);
        setListItems(
            stateWorkflows
                .sort((a, b) => {
                    return a.priority > b.priority ? 1 : -1;
                })
                .map((wf) => wf.uuid)
        );
    }, [stateWorkflows]);

    return (
        <div className="flex h-full w-full flex-col">
            <div className="flex flex-grow flex-row overflow-auto p-4">
                <div className="w-full">
                    <div className="space-y-4">
                        <div>
                            <p className="text-sm text-gray-800">
                                Workflow can be used by triggers to automate
                                content flow and publishing.
                            </p>
                        </div>
                        <div>
                            <div className="divide-y divide-gray-200">
                                <DragDropContext onDragEnd={onDragEnd}>
                                    <Droppable droppableId="workflows">
                                        {(provided, snapshot) => (
                                            <div
                                                {...provided.droppableProps}
                                                ref={provided.innerRef}
                                            >
                                                {listItems.map(
                                                    (item, index) => (
                                                        <Draggable
                                                            key={item}
                                                            draggableId={item}
                                                            index={index}
                                                        >
                                                            {(
                                                                provided,
                                                                snapshot
                                                            ) => (
                                                                <div
                                                                    ref={
                                                                        provided.innerRef
                                                                    }
                                                                    {...provided.draggableProps}
                                                                >
                                                                    <div className="flex-cols my-2 flex items-center space-x-2 rounded bg-white p-2 shadow">
                                                                        <div
                                                                            {...provided.dragHandleProps}
                                                                            aria-label="Drag to reorder"
                                                                            className="mr-5"
                                                                        >
                                                                            <SwitchVerticalIcon className="h-6 w-6 text-gray-600" />
                                                                        </div>
                                                                        <div className="flex-grow">
                                                                            <LabelEdit
                                                                                id={`workflow-${item}`}
                                                                                name={`workflow-${item}`}
                                                                                type="text"
                                                                                onChange={(
                                                                                    e
                                                                                ) => {
                                                                                    const newWorkflows =
                                                                                        workflows.map(
                                                                                            (
                                                                                                wf
                                                                                            ) => {
                                                                                                if (
                                                                                                    wf.uuid ===
                                                                                                    item
                                                                                                ) {
                                                                                                    return {
                                                                                                        ...wf,
                                                                                                        name: e
                                                                                                            .target
                                                                                                            .value,
                                                                                                    };
                                                                                                }
                                                                                                return wf;
                                                                                            }
                                                                                        );
                                                                                    setWorkflows(
                                                                                        newWorkflows
                                                                                    );
                                                                                }}
                                                                                value={
                                                                                    workflows.find(
                                                                                        (
                                                                                            wf
                                                                                        ) =>
                                                                                            wf.uuid ===
                                                                                            item
                                                                                    )
                                                                                        ?.name ||
                                                                                    item
                                                                                }
                                                                            />
                                                                        </div>
                                                                        <div>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() =>
                                                                                    requestRemove(
                                                                                        item
                                                                                    )
                                                                                }
                                                                            >
                                                                                <TrashIcon className="h-6 w-6 text-gray-600" />
                                                                            </button>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    )
                                                )}
                                                {provided.placeholder}
                                            </div>
                                        )}
                                    </Droppable>
                                </DragDropContext>
                            </div>
                            <div className="mt-4 flex space-x-4">
                                <input
                                    className="block w-full rounded-md border-gray-300 py-2 px-4 shadow-sm focus:border-green-500 focus:ring-green-500 disabled:bg-white sm:text-sm"
                                    type="text"
                                    name="newWorkflow"
                                    placeholder="New workflow"
                                    ref={newWorkflow}
                                />
                                <SecondaryButton onClick={addWorkflowStep}>
                                    Add
                                </SecondaryButton>
                            </div>

                            <hr className="my-5" />
                        </div>
                    </div>
                </div>
            </div>
            <div className="w-full bg-gray-50 p-2 text-right">
                <PrimaryButton onClick={handleSave}>Save Changes</PrimaryButton>
            </div>
        </div>
    );
}
